/* AKKA HOME — Personalised Image tile preview engine
   Converts a customer photo into a hand-sketch line rendering, tinted in a
   colourway's paint tones, and composites it onto a photographed tile base.
   Pure client-side (canvas). Shared by the prototype page and the theme section.

   API:
     AkkaTilePreview.COLOURWAYS                     — colourway definitions
     AkkaTilePreview.renderMotif(srcCanvas, opts)   — square photo canvas -> motif canvas (transparent bg)
     AkkaTilePreview.compose(target, baseImg, motifCanvas, opts) — draw base tile + motif
*/
(function (root) {
  'use strict';

  /* Paint tones sampled from photographs of real painted tiles (stroke = dense
     brush line, wash = watery fill). Deliberately softer than the brand swatch
     hexes — glaze mutes pigment. */
  var COLOURWAYS = {
    'Dutch':       { name: 'Dutch Blue',       stroke: [ 61,  78, 130], wash: [125, 140, 175] },
    'Potters':     { name: 'Potters Green',    stroke: [ 34,  62,  47], wash: [113, 131, 117] },
    'New England': { name: 'New England Red',  stroke: [111,  27,  30], wash: [168, 106, 108] }
  };

  /* ---------- small helpers ---------- */

  function makeCanvas(w, h) {
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    return c;
  }

  /* Separable box blur x3 ≈ gaussian. Operates on Float32Array in place-ish. */
  function boxesForGauss(sigma, n) {
    var wIdeal = Math.sqrt((12 * sigma * sigma / n) + 1);
    var wl = Math.floor(wIdeal); if (wl % 2 === 0) wl--;
    var wu = wl + 2;
    var mIdeal = (12 * sigma * sigma - n * wl * wl - 4 * n * wl - 3 * n) / (-4 * wl - 4);
    var m = Math.round(mIdeal);
    var sizes = [];
    for (var i = 0; i < n; i++) sizes.push(i < m ? wl : wu);
    return sizes;
  }
  function boxBlurH(src, dst, w, h, r) {
    var iarr = 1 / (r + r + 1);
    for (var i = 0; i < h; i++) {
      var ti = i * w, li = ti, ri = ti + r;
      var fv = src[ti], lv = src[ti + w - 1], val = (r + 1) * fv;
      for (var j = 0; j < r; j++) val += src[ti + j];
      for (j = 0; j <= r; j++) { val += src[ri++] - fv; dst[ti++] = val * iarr; }
      for (j = r + 1; j < w - r; j++) { val += src[ri++] - src[li++]; dst[ti++] = val * iarr; }
      for (j = w - r; j < w; j++) { val += lv - src[li++]; dst[ti++] = val * iarr; }
    }
  }
  function boxBlurV(src, dst, w, h, r) {
    var iarr = 1 / (r + r + 1);
    for (var i = 0; i < w; i++) {
      var ti = i, li = ti, ri = ti + r * w;
      var fv = src[ti], lv = src[ti + w * (h - 1)], val = (r + 1) * fv;
      for (var j = 0; j < r; j++) val += src[ti + j * w];
      for (j = 0; j <= r; j++) { val += src[ri] - fv; dst[ti] = val * iarr; ri += w; ti += w; }
      for (j = r + 1; j < h - r; j++) { val += src[ri] - src[li]; dst[ti] = val * iarr; li += w; ri += w; ti += w; }
      for (j = h - r; j < h; j++) { val += lv - src[li]; dst[ti] = val * iarr; li += w; ti += w; }
    }
  }
  function gaussBlur(src, w, h, sigma) {
    if (sigma <= 0) return src.slice ? src.slice() : new Float32Array(src);
    var boxes = boxesForGauss(sigma, 3);
    var a = new Float32Array(src), b = new Float32Array(src.length);
    for (var i = 0; i < 3; i++) {
      var r = (boxes[i] - 1) / 2;
      boxBlurH(a, b, w, h, r);
      boxBlurV(b, a, w, h, r);
    }
    return a;
  }

  /* Deterministic PRNG so a given photo renders identically every time. */
  function mulberry32(seed) {
    return function () {
      seed |= 0; seed = (seed + 0x6D2B79F5) | 0;
      var t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }
  function noiseField(w, h, sigma, seed) {
    var rnd = mulberry32(seed);
    var n = new Float32Array(w * h);
    for (var i = 0; i < n.length; i++) n[i] = rnd();
    n = gaussBlur(n, w, h, sigma);
    /* normalise to -1..1 */
    var mn = 1e9, mx = -1e9;
    for (i = 0; i < n.length; i++) { if (n[i] < mn) mn = n[i]; if (n[i] > mx) mx = n[i]; }
    var s = mx > mn ? 2 / (mx - mn) : 0;
    for (i = 0; i < n.length; i++) n[i] = (n[i] - mn) * s - 1;
    return n;
  }

  var clamp = function (v, a, b) { return v < a ? a : v > b ? b : v; };

  /* ---------- background removal ----------
     Two conditions must both hold for a pixel to be backdrop:
       1. its colour is close to the photo's border-colour model (the dominant
          colours of a ring around the edge of the crop), and
       2. it is CONNECTED to the border through other such pixels.
     Pure edge-continuity fills leak through soft/defocused edges; a global
     colour model alone would delete subject areas that happen to match the
     backdrop. Together they cut clean. Returns a feathered foreground mask
     (1 = keep). tol 0..1 scales how aggressive the trim is. */
  function foregroundMask(img, W, H, tol) {
    var thr2 = Math.pow(22 + tol * 58, 2);   /* distance to border model */

    /* border-colour model: quantise a 6px ring into 16³ bins, keep the
       dominant bins (85% coverage, max 8 centroids) */
    var counts = new Uint32Array(4096);
    var sumR = new Float64Array(4096), sumG = new Float64Array(4096), sumB = new Float64Array(4096);
    var ring = 6, total = 0;
    function collect(x, y) {
      var p = (y * W + x) * 4, r = img[p], g = img[p + 1], b = img[p + 2];
      var k = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
      counts[k]++; sumR[k] += r; sumG[k] += g; sumB[k] += b; total++;
    }
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        if (x < ring || y < ring || x >= W - ring || y >= H - ring) collect(x, y);
      }
    }
    var order = [];
    for (var k = 0; k < 4096; k++) if (counts[k]) order.push(k);
    order.sort(function (a, b) { return counts[b] - counts[a]; });
    var cents = [], covered = 0;
    for (var c = 0; c < order.length && cents.length < 8 && covered < total * 0.85; c++) {
      k = order[c];
      cents.push([sumR[k] / counts[k], sumG[k] / counts[k], sumB[k] / counts[k]]);
      covered += counts[k];
    }

    /* candidate pixels: close to any border centroid */
    var cand = new Uint8Array(W * H);
    for (var i = 0, p = 0; i < cand.length; i++, p += 4) {
      var r = img[p], g = img[p + 1], b = img[p + 2];
      for (c = 0; c < cents.length; c++) {
        var dr = r - cents[c][0], dg = g - cents[c][1], db = b - cents[c][2];
        if (dr * dr + dg * dg + db * db < thr2) { cand[i] = 1; break; }
      }
    }

    /* flood from the border through candidates only */
    var mask = new Uint8Array(W * H);        /* 1 = background */
    var stack = new Int32Array(W * H);
    var sp = 0;
    function push(j) { if (!mask[j] && cand[j]) { mask[j] = 1; stack[sp++] = j; } }
    for (x = 0; x < W; x++) { push(x); push((H - 1) * W + x); }
    for (y = 0; y < H; y++) { push(y * W); push(y * W + W - 1); }
    while (sp) {
      i = stack[--sp];
      var ix = i % W, iy = (i / W) | 0;
      if (ix + 1 < W) push(i + 1);
      if (ix > 0) push(i - 1);
      if (iy + 1 < H) push(i + W);
      if (iy > 0) push(i - W);
    }
    var fg = new Float32Array(W * H);
    var kept = 0;
    for (i = 0; i < fg.length; i++) { fg[i] = mask[i] ? 0 : 1; kept += fg[i]; }
    /* Sanity guard: if the "backdrop" would swallow nearly the whole crop the
       subject is probably filling the frame — stand down rather than erase it. */
    if (kept < fg.length * 0.08) return null;
    return gaussBlur(fg, W, H, 2);           /* feather the cut edge */
  }

  /* ---------- the sketch pipeline ---------- */

  /* srcCanvas: square canvas holding the photo crop.
     opts: { colour:'Dutch', detail:0..1, wash:bool, feather:bool,
             removeBg:bool, bgTol:0..1 } */
  function renderMotif(srcCanvas, opts) {
    opts = opts || {};
    var cw = COLOURWAYS[opts.colour] || COLOURWAYS['Dutch'];
    var detail = opts.detail == null ? 0.55 : clamp(opts.detail, 0, 1);
    var washOn = opts.wash !== false;
    var featherOn = opts.feather !== false;

    var W = srcCanvas.width, H = srcCanvas.height;
    var ctx = srcCanvas.getContext('2d');
    var img = ctx.getImageData(0, 0, W, H).data;

    var fgMask = opts.removeBg ? foregroundMask(img, W, H, opts.bgTol == null ? 0.5 : clamp(opts.bgTol, 0, 1)) : null;

    /* grayscale */
    var g = new Float32Array(W * H);
    for (var i = 0, p = 0; i < g.length; i++, p += 4) {
      g[i] = (0.299 * img[p] + 0.587 * img[p + 1] + 0.114 * img[p + 2]) / 255;
    }

    /* percentile normalise (2–98%) so dim phone photos still draw */
    var hist = new Uint32Array(256);
    for (i = 0; i < g.length; i++) hist[(g[i] * 255) | 0]++;
    var lo = 0, hiv = 255, acc = 0, cut = g.length * 0.02;
    for (i = 0; i < 256; i++) { acc += hist[i]; if (acc > cut) { lo = i; break; } }
    acc = 0;
    for (i = 255; i >= 0; i--) { acc += hist[i]; if (acc > cut) { hiv = i; break; } }
    var span = Math.max(20, hiv - lo) / 255;
    var lo1 = lo / 255;
    for (i = 0; i < g.length; i++) g[i] = clamp((g[i] - lo1) / span, 0, 1);

    /* hand-wobble: warp the field slightly before edge extraction */
    var nx = noiseField(W, H, 7, 101), ny = noiseField(W, H, 7, 202);
    var amp = W * 0.004;
    var gw = new Float32Array(W * H);
    for (var y = 0; y < H; y++) {
      for (var x = 0; x < W; x++) {
        var sx = clamp(Math.round(x + nx[y * W + x] * amp), 0, W - 1);
        var sy = clamp(Math.round(y + ny[y * W + x] * amp), 0, H - 1);
        gw[y * W + x] = g[sy * W + sx];
      }
    }

    /* XDoG line extraction */
    var sigma = 2.6 - detail * 1.8;               /* detail 0 → 2.6, 1 → 0.8 */
    var g1 = gaussBlur(gw, W, H, sigma);
    var g2 = gaussBlur(gw, W, H, sigma * 1.6);
    var eps = 0.012, phi = 45;
    var line = new Float32Array(W * H);
    for (i = 0; i < line.length; i++) {
      var d = g1[i] - 0.97 * g2[i];
      var e = d >= eps ? 1 : 1 + Math.tanh(phi * (d - eps));
      var a = 1 - clamp(e, 0, 1);
      line[i] = a < 0.08 ? 0 : a;
    }

    /* watery two-level wash from broad tone */
    var wash = null;
    if (washOn) {
      var wl = gaussBlur(gw, W, H, 5);
      wash = new Float32Array(W * H);
      for (i = 0; i < wash.length; i++) {
        var v = wl[i];
        wash[i] = v < 0.36 ? 0.30 : v < 0.60 ? 0.15 : 0;
      }
      /* blur the wash boundary a touch so it pools like pigment */
      wash = gaussBlur(wash, W, H, 1.2);
    }

    /* brush-density noise + square feather mask */
    var an = noiseField(W, H, 3, 303);
    var out = makeCanvas(W, H);
    var octx = out.getContext('2d');
    var od = octx.createImageData(W, H);
    var o = od.data;
    var sR = cw.stroke[0], sG = cw.stroke[1], sB = cw.stroke[2];
    var wR = cw.wash[0], wG = cw.wash[1], wB = cw.wash[2];

    for (y = 0; y < H; y++) {
      /* feather: fade to nothing over the outer 9% of the crop */
      var fy = featherOn ? clamp(Math.min(y, H - 1 - y) / (H * 0.09), 0, 1) : 1;
      for (x = 0; x < W; x++) {
        i = y * W + x;
        var f = featherOn ? Math.min(fy, clamp(Math.min(x, W - 1 - x) / (W * 0.09), 0, 1)) : 1;
        f = f * f * (3 - 2 * f); /* smoothstep */
        if (fgMask) f *= fgMask[i];
        var la = line[i] * (0.82 + 0.22 * an[i]);
        var wa = wash ? wash[i] * (0.8 + 0.3 * an[(i + 7919) % an.length]) : 0;
        var alpha = clamp(la + wa, 0, 0.94) * f;
        p = i * 4;
        if (alpha <= 0.01) { o[p + 3] = 0; continue; }
        var t = la / (la + wa + 1e-6);           /* 1 → pure stroke colour */
        var dk = clamp(la * 1.15, 0, 1);          /* dense lines go darker  */
        var r = wR + (sR - wR) * t, gg = wG + (sG - wG) * t, b = wB + (sB - wB) * t;
        r = r * (1 - 0.18 * dk); gg = gg * (1 - 0.18 * dk); b = b * (1 - 0.18 * dk);
        o[p] = r; o[p + 1] = gg; o[p + 2] = b; o[p + 3] = alpha * 255;
      }
    }
    octx.putImageData(od, 0, 0);
    return out;
  }

  /* Composite base tile photo + motif. target sized square canvas.
     The photographed tile face occupies the central TILE_FRAC of the product
     image (measured: 0.462, centred). opts.motifFrac sizes the motif zone as a
     fraction of the tile face (default .55 — matches the painted examples). */
  var TILE_FRAC = 0.462;
  function compose(target, baseImg, motifCanvas, opts) {
    opts = opts || {};
    var S = target.width;
    var ctx = target.getContext('2d');
    ctx.clearRect(0, 0, S, S);
    ctx.drawImage(baseImg, 0, 0, S, S);
    if (motifCanvas) {
      var m = S * TILE_FRAC * (opts.motifFrac || 0.55);
      var off = (S - m) / 2;
      ctx.save();
      ctx.globalCompositeOperation = 'multiply';
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(motifCanvas, off, off, m, m);
      ctx.restore();
    }
    return target;
  }

  root.AkkaTilePreview = {
    COLOURWAYS: COLOURWAYS,
    renderMotif: renderMotif,
    compose: compose
  };
})(typeof window !== 'undefined' ? window : this);
